// src/PlayerList.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { POSITION_KEYS, DB_TO_TRANSLATION_KEY } from '../utils/positions';
import { normalizeRoleValue } from '../utils/legacyMapping';
import { canUseProspectFilter, currentUserCanUseProspectFilter } from '../utils/prospectUtils';
import { PLAYER_TYPE, matchesPlayerType } from '../utils/playerTypes';
import {
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import {
  FilterList,
  SportsBasketball,
  Straighten,
  InsertChart,
  FormatListBulleted,
  BarChart,
  CalendarMonth,
  RestartAlt
} from '@mui/icons-material';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { cardStatic, PALETTE } from '../theme/designSystem';
import { useClubTheme } from '../contexts/ClubThemeContext';

// Modèle (ne pas modifier) :
import Player from '../models/playerModel';
import { PlayerComparisonProvider } from '../context/PlayerComparisonContext';

// Filtres basiques :
import PlayerFilters from '../playerListComponents/PlayerFilters';
// Filtres de caractéristiques :
import PlayerFiltersCaracterísticas from '../playerListComponents/PlayerFiltersCaracterísticas';
// Filtres de mesures :
import PlayerFiltersMedidas from '../playerListComponents/PlayerFiltersMedidas';
// Filtres de statistiques (nouveau) :
import PlayerFiltersEstadisticas from '../playerListComponents/PlayerFiltersEstadisticas';

// Résultats liste 
import PlayerResults from '../playerListComponents/PlayerResults';
// Résultats mesures
import MedidasResults from '../playerListComponents/MedidasResults';
// Résultats calendrier (pour admin pro / super admin) 
import CalendarioResults from '../playerListComponents/CalendarioResults';
// Résultats statistiques
import StatsResults from '../playerListComponents/StatsResults';

// -----------------------------------------------------
// (Ne pas toucher) Fonction utilitaire pour calculer l'âge
// -----------------------------------------------------
const calculateAge = (dateNaissance) => {
  if (!dateNaissance) return 0;
  const birthDate = new Date(dateNaissance);
  const today = new Date();
  let years = today.getFullYear() - birthDate.getFullYear();
  const diffTime = Math.abs(today - birthDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const decimal = (diffDays % 365.25) / 365.25;

  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    years--;
  }
  return years + decimal;
};


// -----------------------------------------------------
// Nouvelle approche pour les filtres de statistiques : 
// Vérifier si un joueur a au moins UNE compétition 
// qui remplit la condition >= pour chaque statistique.
// -----------------------------------------------------
const checkStatFilter = (statsArray, filterValue, fieldName) => {
  if (!filterValue) return true; // Pas de filtre => passe toujours
  const numericFilter = parseFloat(filterValue);
  if (isNaN(numericFilter)) return true; // si l'utilisateur a mis autre chose qu'un nombre
  if (!statsArray || statsArray.length === 0) return false; // pas de stats => ne passe pas

  // On veut au moins UNE compétition où la valeur est >= numericFilter
  return statsArray.some((comp) => {
    const statValue = parseFloat(comp[fieldName]) || 0;
    return statValue >= numericFilter;
  });
};

const PlayerList = () => {
  const { t } = useTranslation();

  // --- États généraux ---
  const [players, setPlayers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupColors, setGroupColors] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [informeFilter, setInformeFilter] = useState(null);
  const [scoutFilter, setScoutFilter] = useState(null);

  // --- Filtres "basiques" ---
  const [nameFilter, setNameFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState([]);
  const [groupFilter, setGroupFilter] = useState([]);
  const [leagueFilter, setLeagueFilter] = useState([]);
  const [competitionFilter, setCompetitionFilter] = useState([]);
  const [heightFilter, setHeightFilter] = useState([100, 230]);
  const [ageFilter, setAgeFilter] = useState([8, 42]);
  const [playerTypeFilter, setPlayerTypeFilter] = useState(PLAYER_TYPE.ALL);
  const [licenciaFilter, setLicenciaFilter] = useState([]);
  const [agenciaFilter, setAgenciaFilter] = useState([]);

  // --- Filtres "caractéristiques" ---
  const [rolOfensivoFilter, setRolOfensivoFilter] = useState([]);
  const [manoDominanteFilter, setManoDominanteFilter] = useState([]);
  const [categoriaFilter, setCategoriaFilter] = useState([]);
  const [shootingRangeFilter, setShootingRangeFilter] = useState([]);
  const [rolDefensivoFilter, setRolDefensivoFilter] = useState([]);
  const [jeJiTypeFilter, setJeJiTypeFilter] = useState('');
  const [jeJiAttributesFilter, setJeJiAttributesFilter] = useState([]);
  const [currentVBCRatingFilter, setCurrentVBCRatingFilter] = useState([]);
  const [proyeccionVBCRatingFilter, setProyeccionVBCRatingFilter] = useState([]);
  const [flagBehaviourFilter, setFlagBehaviourFilter] = useState([]);
  const [injuryProneFilter, setInjuryProneFilter] = useState([]);
  const [digitalBehaviourFilter, setDigitalBehaviourFilter] = useState([]);
  const [comparacionProFilter, setComparacionProFilter] = useState('');

  // --- Filtres "nacionalidad" ---
  const [nacionalidadFilter, setNacionalidadFilter] = useState('');
  const [segundaNacionalidadFilter, setSegundaNacionalidadFilter] = useState('');
  const [origenEtnicoFilter, setOrigenEtnicoFilter] = useState([]);

  // --- Filtres "situation" ---
  const [seleccionNacionalFilter, setSeleccionNacionalFilter] = useState('');
  const [equipoActualFilter, setEquipoActualFilter] = useState([]);
  const [competicionFilter, setCompeticionFilter] = useState([]);

  // --- Filtres "expérience" ---
  const [añoDraftFilter, setAñoDraftFilter] = useState('');
  const [posicionDraftFilter, setPosicionDraftFilter] = useState([]);
  const [equipoPreDraftFilter, setEquipoPreDraftFilter] = useState('');
  const [pitFilter, setPitFilter] = useState('');
  const [eurocampFilter, setEurocampFilter] = useState('');
  const [slvegasFilter, setSlvegasFilter] = useState('');
  const [nbaExperienceFilter, setNbaExperienceFilter] = useState([]);
  const [experienciaAcbFilter, setExperienciaAcbFilter] = useState([]);
  const [experiencaEuropaFilter, setExperiencaEuropaFilter] = useState([]);
  const [experienca_fueraFilter, setExperienca_fueraFilter] = useState([]);
  const [experienca_ncaaFilter, setExperienca_ncaaFilter] = useState([]);

  // --- Filtres "contrat" ---
  const [rangoPreciosFilter, setRangoPreciosFilter] = useState([]);
  const [contratoFilter, setContratoFilter] = useState([]);
  const [buyoutFilter, setBuyoutFilter] = useState([]);

  // --- Filtres "mesures" ---
  const [alturaFilter, setAlturaFilter] = useState('');
  const [alturaZapatosFilter, setAlturaZapatosFilter] = useState('');
  const [envergaduraFilter, setEnvergaduraFilter] = useState('');
  const [alcanceFilter, setAlcanceFilter] = useState('');
  const [saltoVerticalMaxFilter, setSaltoVerticalMaxFilter] = useState('');
  const [saltoVerticalPieFilter, setSaltoVerticalPieFilter] = useState('');
  const [medidasPesoFilter, setMedidasPesoFilter] = useState('');
  const [grasaCorporalFilter, setGrasaCorporalFilter] = useState('');
  const [longitudManoFilter, setLongitudManoFilter] = useState('');
  const [anchoManoFilter, setAnchoManoFilter] = useState('');
  const [pressBancaFilter, setPressBancaFilter] = useState('');
  const [agilidadFilter, setAgilidadFilter] = useState('');
  const [sprintCuartoFilter, setSprintCuartoFilter] = useState('');

  // --- Filtres "estadisticas" (NOUVEAU) ---
  const [gamesFilter, setGamesFilter] = useState('');
  const [minutesFilter, setMinutesFilter] = useState('');
  const [pointsFilter, setPointsFilter] = useState('');
  const [twoFGPercentFilter, setTwoFGPercentFilter] = useState('');
  const [threeFGPercentFilter, setThreeFGPercentFilter] = useState('');
  const [freeThrowPercentFilter, setFreeThrowPercentFilter] = useState('');
  const [rebOffFilter, setRebOffFilter] = useState('');
  const [rebDefFilter, setRebDefFilter] = useState('');
  const [rebTotFilter, setRebTotFilter] = useState('');
  const [assistsFilter, setAssistsFilter] = useState('');
  const [foulsFilter, setFoulsFilter] = useState('');
  const [blocksFilter, setBlocksFilter] = useState('');
  const [stealsFilter, setStealsFilter] = useState('');
  const [turnoversFilter, setTurnoversFilter] = useState('');
  const [rankingFilter, setRankingFilter] = useState('');

  // Divers
  const [agencies, setAgencies] = useState([]);
  const [uniqueLeagues, setUniqueLeagues] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState(null);
  const [selectedPlayerIDs, setSelectedPlayerIDs] = useState([]);
  const [multiDeleteDialogOpen, setMultiDeleteDialogOpen] = useState(false);
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState(null);

  // Sous PRO, on écarte le vivier « NCAA + » (NCAA, High School, Overtime Elite,
  // NBA Academy, PIT) même pour un joueur typé pro — mais SEULEMENT pour les rôles
  // qui voient ce bucket : les autres n'auraient aucun filtre où les retrouver.
  // Rôle relu depuis localStorage, comme la purge de persistance : l'état userRole
  // est renseigné par un useEffect séparé, donc absent au premier rendu.
  const excludeProspectsFromPro = useMemo(currentUserCanUseProspectFilter, []);

  const [verdictFilter, setVerdictFilter] = useState([]);

  // État pour les onglets de filtres (NOUVEAU)
  const [filterTab, setFilterTab] = useState(0);

  // État pour les onglets de résultats
  const [activeTab, setActiveTab] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { primaryColor } = useClubTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;
  const mediaUrl = getMediaBaseUrl();
  const timestamp = Date.now();

  const [currentUserId, setCurrentUserId] = useState(null);
  const [competitionDetailFilter, setCompetitionDetailFilter] = useState(null); // null, 'with', ou 'without'

  // États pour les comptages de rôles et autres attributs
  const [rolOfensivoCounts, setRolOfensivoCounts] = useState({});
  const [rolDefensivoCounts, setRolDefensivoCounts] = useState({});
  const [manoDominanteCounts, setManoDominanteCounts] = useState({});
  const [categoriaCounts, setCategoriaCounts] = useState({});
  const [shootingRangeCounts, setShootingRangeCounts] = useState({});
  const [jeJiTypeCounts, setJeJiTypeCounts] = useState({});
  const [currentVBCRatingCounts, setCurrentVBCRatingCounts] = useState({});
  const [proyeccionVBCRatingCounts, setProyeccionVBCRatingCounts] = useState({});
  const [flagBehaviourCounts, setFlagBehaviourCounts] = useState({});
  const [injuryProneCounts, setInjuryProneCounts] = useState({});
  const [digitalBehaviourCounts, setDigitalBehaviourCounts] = useState({});
  const [origenEtnicoCounts, setOrigenEtnicoCounts] = useState({});
  const [competicionCounts, setCompeticionCounts] = useState({});
  const [posicionDraftCounts, setPosicionDraftCounts] = useState({});
  const [nbaExperienceCounts, setNbaExperienceCounts] = useState({});
  const [experienciaAcbCounts, setExperienciaAcbCounts] = useState({});
  const [experienciaEuropaCounts, setExperienciaEuropaCounts] = useState({});
  const [experienciaFueraCounts, setExperienciaFueraCounts] = useState({});
  const [experienciaNcaaCounts, setExperienciaNcaaCounts] = useState({});
  const [rangoPreciosCounts, setRangoPreciosCounts] = useState({});
  const [contratoCounts, setContratoCounts] = useState({});
  const [buyoutCounts, setBuyoutCounts] = useState({});
  const [licenciaCounts, setLicenciaCounts] = useState({});
  const [agenciaCounts, setAgenciaCounts] = useState({});
  const [leagueCounts, setLeagueCounts] = useState({});
  const [competitionCounts, setCompetitionCounts] = useState({});
  const [jeAttributeCounts, setJeAttributeCounts] = useState({});
  const [jiAttributeCounts, setJiAttributeCounts] = useState({});
  const [nacionalidadCounts, setNacionalidadCounts] = useState({});
  const [segundaNacionalidadCounts, setSegundaNacionalidadCounts] = useState({});
  const [seleccionNacionalCounts, setSeleccionNacionalCounts] = useState({});

  // Fonction pour obtenir la clé de position à partir de la valeur en DB
  const getPositionKey = (dbValue) => {
    if (!dbValue) return null;
    // Si c'est déjà une clé valide
    if (POSITION_KEYS.includes(dbValue)) return dbValue;
    // Sinon chercher dans le mapping
    return DB_TO_TRANSLATION_KEY[dbValue] || null;
  };

  // Récupération du rôle utilisateur depuis localStorage
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setUserRole(user?.role?.toLowerCase());
    setCurrentUserId(user?.id); // Ajout de cette ligne
  }, []);

  // Fonction pour sauvegarder les filtres dans localStorage
  const saveFiltersToLocalStorage = () => {
    const filters = {
      // Filtres basiques
      nameFilter,
      yearFilter,
      positionFilter,
      groupFilter,
      leagueFilter,
      competitionFilter,
      heightFilter,
      ageFilter,
      playerTypeFilter,
      licenciaFilter,
      agenciaFilter,
      informeFilter,
      scoutFilter,
      verdictFilter,
      competitionDetailFilter,
      // Filtres caractéristiques
      rolOfensivoFilter,
      manoDominanteFilter,
      categoriaFilter,
      shootingRangeFilter,
      rolDefensivoFilter,
      jeJiTypeFilter,
      jeJiAttributesFilter,
      currentVBCRatingFilter,
      proyeccionVBCRatingFilter,
      flagBehaviourFilter,
      injuryProneFilter,
      digitalBehaviourFilter,
      comparacionProFilter,
      // Filtres nationalité
      nacionalidadFilter,
      segundaNacionalidadFilter,
      origenEtnicoFilter,
      // Filtres situation
      seleccionNacionalFilter,
      equipoActualFilter,
      competicionFilter,
      // Filtres expérience
      añoDraftFilter,
      posicionDraftFilter,
      equipoPreDraftFilter,
      pitFilter,
      eurocampFilter,
      slvegasFilter,
      nbaExperienceFilter,
      experienciaAcbFilter,
      experiencaEuropaFilter,
      experienca_fueraFilter,
      experienca_ncaaFilter,
      // Filtres contrat
      rangoPreciosFilter,
      contratoFilter,
      buyoutFilter,
      // Filtres mesures
      alturaFilter,
      alturaZapatosFilter,
      envergaduraFilter,
      alcanceFilter,
      saltoVerticalMaxFilter,
      saltoVerticalPieFilter,
      medidasPesoFilter,
      grasaCorporalFilter,
      longitudManoFilter,
      anchoManoFilter,
      pressBancaFilter,
      agilidadFilter,
      sprintCuartoFilter,
      // Filtres estadísticas
      gamesFilter,
      minutesFilter,
      pointsFilter,
      twoFGPercentFilter,
      threeFGPercentFilter,
      freeThrowPercentFilter,
      rebOffFilter,
      rebDefFilter,
      rebTotFilter,
      assistsFilter,
      foulsFilter,
      blocksFilter,
      stealsFilter,
      turnoversFilter,
      rankingFilter,
      // Tab actuel
      filterTab,
      activeTab
    };
    localStorage.setItem('playerListFilters', JSON.stringify(filters));
  };

  // Fonction pour charger les filtres depuis localStorage
  const loadFiltersFromLocalStorage = () => {
    const savedFilters = localStorage.getItem('playerListFilters');
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        // Filtres basiques
        setNameFilter(filters.nameFilter || '');
        setYearFilter(filters.yearFilter || '');
        setPositionFilter(filters.positionFilter || []);
        setGroupFilter(filters.groupFilter || []);
        setLeagueFilter(filters.leagueFilter || []);
        setCompetitionFilter(filters.competitionFilter || []);
        setHeightFilter(filters.heightFilter || [100, 230]);
        setAgeFilter(filters.ageFilter || [8, 42]);
        // Le filtre NCAA est réservé à super admin / admin pro : on purge une valeur
        // persistée par un autre rôle (sinon liste vide avec un bouton invisible).
        // Le rôle est relu depuis localStorage : l'état userRole n'est pas encore
        // renseigné au premier rendu (useEffect séparé).
        const savedPlayerType = filters.playerTypeFilter || PLAYER_TYPE.ALL;
        const savedUserRole = JSON.parse(localStorage.getItem('user') || 'null')?.role;
        setPlayerTypeFilter(
          savedPlayerType === PLAYER_TYPE.NCAA && !canUseProspectFilter(savedUserRole)
            ? PLAYER_TYPE.ALL
            : savedPlayerType
        );
        setLicenciaFilter(filters.licenciaFilter || []);
        setAgenciaFilter(filters.agenciaFilter || []);
        setInformeFilter(filters.informeFilter || '');
        setScoutFilter(filters.scoutFilter || '');
        setVerdictFilter(filters.verdictFilter || []);
        setCompetitionDetailFilter(filters.competitionDetailFilter || null);
        // Filtres caractéristiques
        setRolOfensivoFilter(filters.rolOfensivoFilter || []);
        setManoDominanteFilter(filters.manoDominanteFilter || []);
        setCategoriaFilter(filters.categoriaFilter || []);
        setShootingRangeFilter(filters.shootingRangeFilter || []);
        setRolDefensivoFilter(filters.rolDefensivoFilter || []);
        setJeJiTypeFilter(filters.jeJiTypeFilter || '');
        setJeJiAttributesFilter(filters.jeJiAttributesFilter || []);
        setCurrentVBCRatingFilter(filters.currentVBCRatingFilter || []);
        setProyeccionVBCRatingFilter(filters.proyeccionVBCRatingFilter || []);
        setFlagBehaviourFilter(filters.flagBehaviourFilter || []);
        setInjuryProneFilter(filters.injuryProneFilter || []);
        setDigitalBehaviourFilter(filters.digitalBehaviourFilter || []);
        setComparacionProFilter(filters.comparacionProFilter || '');
        // Filtres nationalité
        setNacionalidadFilter(filters.nacionalidadFilter || '');
        setSegundaNacionalidadFilter(filters.segundaNacionalidadFilter || '');
        setOrigenEtnicoFilter(filters.origenEtnicoFilter || []);
        // Filtres situation
        setSeleccionNacionalFilter(filters.seleccionNacionalFilter || '');
        setEquipoActualFilter(filters.equipoActualFilter || []);
        setCompeticionFilter(filters.competicionFilter || []);
        // Filtres expérience
        setAñoDraftFilter(filters.añoDraftFilter || '');
        setPosicionDraftFilter(filters.posicionDraftFilter || []);
        setEquipoPreDraftFilter(filters.equipoPreDraftFilter || '');
        setPitFilter(filters.pitFilter || '');
        setEurocampFilter(filters.eurocampFilter || '');
        setSlvegasFilter(filters.slvegasFilter || '');
        setNbaExperienceFilter(filters.nbaExperienceFilter || []);
        setExperienciaAcbFilter(filters.experienciaAcbFilter || []);
        setExperiencaEuropaFilter(filters.experiencaEuropaFilter || []);
        setExperienca_fueraFilter(filters.experienca_fueraFilter || []);
        setExperienca_ncaaFilter(filters.experienca_ncaaFilter || []);
        // Filtres contrat
        setRangoPreciosFilter(filters.rangoPreciosFilter || []);
        setContratoFilter(filters.contratoFilter || []);
        setBuyoutFilter(filters.buyoutFilter || []);
        // Filtres mesures
        setAlturaFilter(filters.alturaFilter || '');
        setAlturaZapatosFilter(filters.alturaZapatosFilter || '');
        setEnvergaduraFilter(filters.envergaduraFilter || '');
        setAlcanceFilter(filters.alcanceFilter || '');
        setSaltoVerticalMaxFilter(filters.saltoVerticalMaxFilter || '');
        setSaltoVerticalPieFilter(filters.saltoVerticalPieFilter || '');
        setMedidasPesoFilter(filters.medidasPesoFilter || '');
        setGrasaCorporalFilter(filters.grasaCorporalFilter || '');
        setLongitudManoFilter(filters.longitudManoFilter || '');
        setAnchoManoFilter(filters.anchoManoFilter || '');
        setPressBancaFilter(filters.pressBancaFilter || '');
        setAgilidadFilter(filters.agilidadFilter || '');
        setSprintCuartoFilter(filters.sprintCuartoFilter || '');
        // Filtres estadísticas
        setGamesFilter(filters.gamesFilter || '');
        setMinutesFilter(filters.minutesFilter || '');
        setPointsFilter(filters.pointsFilter || '');
        setTwoFGPercentFilter(filters.twoFGPercentFilter || '');
        setThreeFGPercentFilter(filters.threeFGPercentFilter || '');
        setFreeThrowPercentFilter(filters.freeThrowPercentFilter || '');
        setRebOffFilter(filters.rebOffFilter || '');
        setRebDefFilter(filters.rebDefFilter || '');
        setRebTotFilter(filters.rebTotFilter || '');
        setAssistsFilter(filters.assistsFilter || '');
        setFoulsFilter(filters.foulsFilter || '');
        setBlocksFilter(filters.blocksFilter || '');
        setStealsFilter(filters.stealsFilter || '');
        setTurnoversFilter(filters.turnoversFilter || '');
        setRankingFilter(filters.rankingFilter || '');
        // Tabs
        setFilterTab(filters.filterTab || 0);
        setActiveTab(filters.activeTab || 0);
      } catch (error) {
        console.error('Error loading filters from localStorage:', error);
      }
    }
  };

  // Charger les filtres au montage du composant
  useEffect(() => {
    loadFiltersFromLocalStorage();
  }, []);

  // Sauvegarder les filtres à chaque changement
  useEffect(() => {
    // Ne pas sauvegarder lors du chargement initial
    if (!isInitialLoad) {
      saveFiltersToLocalStorage();
    }
  }, [
    // Filtres basiques
    nameFilter,
    yearFilter,
    positionFilter,
    groupFilter,
    leagueFilter,
    competitionFilter,
    heightFilter,
    ageFilter,
    playerTypeFilter,
    licenciaFilter,
    agenciaFilter,
    informeFilter,
    scoutFilter,
    verdictFilter,
    competitionDetailFilter,
    // Filtres caractéristiques
    rolOfensivoFilter,
    manoDominanteFilter,
    categoriaFilter,
    shootingRangeFilter,
    rolDefensivoFilter,
    jeJiTypeFilter,
    jeJiAttributesFilter,
    currentVBCRatingFilter,
    proyeccionVBCRatingFilter,
    flagBehaviourFilter,
    injuryProneFilter,
    digitalBehaviourFilter,
    comparacionProFilter,
    // Filtres nationalité
    nacionalidadFilter,
    segundaNacionalidadFilter,
    origenEtnicoFilter,
    // Filtres situation
    seleccionNacionalFilter,
    equipoActualFilter,
    competicionFilter,
    // Filtres expérience
    añoDraftFilter,
    posicionDraftFilter,
    equipoPreDraftFilter,
    pitFilter,
    eurocampFilter,
    slvegasFilter,
    nbaExperienceFilter,
    experienciaAcbFilter,
    experiencaEuropaFilter,
    experienca_fueraFilter,
    experienca_ncaaFilter,
    // Filtres contrat
    rangoPreciosFilter,
    contratoFilter,
    buyoutFilter,
    // Filtres mesures
    alturaFilter,
    alturaZapatosFilter,
    envergaduraFilter,
    alcanceFilter,
    saltoVerticalMaxFilter,
    saltoVerticalPieFilter,
    medidasPesoFilter,
    grasaCorporalFilter,
    longitudManoFilter,
    anchoManoFilter,
    pressBancaFilter,
    agilidadFilter,
    sprintCuartoFilter,
    // Filtres estadísticas
    gamesFilter,
    minutesFilter,
    pointsFilter,
    twoFGPercentFilter,
    threeFGPercentFilter,
    freeThrowPercentFilter,
    rebOffFilter,
    rebDefFilter,
    rebTotFilter,
    assistsFilter,
    foulsFilter,
    blocksFilter,
    stealsFilter,
    turnoversFilter,
    rankingFilter,
    filterTab,
    activeTab,
    isInitialLoad
  ]);

  // Lecture initiale de l'URL pour restaurer des filtres (optionnel)
  useEffect(() => {
    if (isInitialLoad) {
      // Logique éventuelle pour relire l'URL si besoin...
      setIsInitialLoad(false);
    }
  }, [location.search, isInitialLoad]);

  // Appel API pour récupérer les joueurs, groupes, agences, etc.
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const user = JSON.parse(localStorage.getItem('user'));
        const roleLower = user?.role?.toLowerCase();
        const typeParam =
          roleLower === 'admin pro'
            ? 'pro'
            : (roleLower === 'admin cantera' || roleLower === 'scout interne cantera' || roleLower === 'scout externe cantera')
            ? 'cantera'
            : null;

        const playersEndpoint =
  user?.role?.toLowerCase() === 'entrenador'
    ? `/api/players/coach/${user.id}`
    : `/api/players${typeParam ? `?type=${typeParam}` : ''}`;

        const [playersResponse, groupsResponse, agenciesResponse] = await Promise.all([
          api.get(playersEndpoint),
          api.get('/api/groups'),
          api.get('/api/players/agencies')
        ]);

        const playersData = playersResponse.data;
        setPlayers(playersData.players.map((item) => Player.fromJSON(item)));

        // Groupes + couleurs
        setGroups(groupsResponse.data);
        const colors = {};
        groupsResponse.data.forEach((group) => {
          colors[group.libelle] = group.code_couleur;
        });
        setGroupColors(colors);

        // Agences
        if (agenciesResponse?.data.agencies?.length) {
          setAgencies(agenciesResponse.data.agencies);
        } else {
          setAgencies([]);
        }

        // Ligues uniques
        const leagues = Array.from(
          new Set(
            playersData.players
              .map((p) => p.league_1)
              .filter((league) => league?.trim() && league.trim() !== 'null')
          )
        ).sort();
        setUniqueLeagues(leagues);
      } catch (err) {
        console.error('Erreur:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isInitialLoad) {
      fetchData();
    }
  }, [backendUrl, section, isInitialLoad]);

  // Fonction pour créer une version filtrée des joueurs pour le calcul des comptages
  // Cette version applique tous les filtres SAUF ceux de caractéristiques pour éviter les boucles
  const getPlayersForCounting = () => {
    return players.filter((player) => {
      const playerAge = calculateAge(player.dateNaissance);
      const playerGroups = player.groupes.map((g) => g.libelle);
      const hasSinGrupos = groupFilter.includes('Sin Grupos');
      const playerHasNoGroups = !player.groupes || player.groupes.length === 0;

      const groupCondition =
        groupFilter.length === 0
          ? true
          : (hasSinGrupos && playerHasNoGroups) ||
            playerGroups.some((g) => groupFilter.includes(g));

      const positionCondition =
        positionFilter.length === 0 ||
        positionFilter.includes(getPositionKey(player.poste));

      const typeCondition = matchesPlayerType(player, playerTypeFilter, { excludeProspects: excludeProspectsFromPro });

      const yearSearchTerm = yearFilter.toLowerCase();
      const yearCondition =
        !yearFilter || (player.anneeNaissance + '').includes(yearSearchTerm);

      const searchTerm = nameFilter.toLowerCase();
      // Vérifier aussi dans les nouvelles équipes
      const hasEquipeMatch = player.competitions_detail && player.competitions_detail.length > 0 &&
        player.competitions_detail.some(c => c.equipe_nom && c.equipe_nom.toLowerCase().includes(searchTerm));

      const nameTeamCondition =
        (!nameFilter && !yearFilter) ||
        (player.nom && player.nom.toLowerCase().includes(searchTerm)) ||
        (player.team && player.team.toLowerCase().includes(searchTerm)) ||
        hasEquipeMatch;

      const leagueCondition =
        leagueFilter.length === 0 || leagueFilter.includes(player.league_1);

      const heightCondition =
        player.taille >= heightFilter[0] && player.taille <= heightFilter[1];
      const ageCondition = playerAge >= ageFilter[0] && playerAge <= ageFilter[1];

      const licenciaCondition =
        licenciaFilter.length === 0 || licenciaFilter.includes(player.pasaporte);
      const agenciaCondition =
        agenciaFilter.length === 0 || agenciaFilter.includes(player.agencia);

      // Retourner true si toutes les conditions non-caractéristiques sont remplies
      return (
        groupCondition &&
        positionCondition &&
        typeCondition &&
        yearCondition &&
        nameTeamCondition &&
        leagueCondition &&
        heightCondition &&
        ageCondition &&
        licenciaCondition &&
        agenciaCondition
      );
    });
  };

  // Calcul des comptages pour tous les attributs de filtres
  useEffect(() => {
    // Utiliser les joueurs pré-filtrés pour le calcul des comptages
    const playersForCounting = getPlayersForCounting();
    // Listes des options possibles
    // Clés de traduction pour les rôles offensifs
    const offensiveRoleKeys = [
      'ballHandler', 'secondaryBallHandler', 'dominantScorer', 'floorGeneral', 'shootingSpecialist',
      'creatorWing', 'versatileWing', 'strongWing', 'slasherWing', 'spotUpWing', 'threeAndD',
      'traditionalPowerForward', 'skilledForward', 'stretchFour',
      'creatorCenter', 'versatileCenter', 'midRangeCenter', 'stretchFive', 'postScorer', 'rimFinisher', 'pickAndRollBig', 'marginalOffensiveRole'
    ];
    const offensiveRoles = offensiveRoleKeys.map(key => t(`createPlayer.offensiveRoles.${key}`));

    // Clés de traduction pour les rôles défensifs
    const defensiveRoleKeys = [
      'pointOfAttack', 'intenseOneOnOne', 'perimeterDefender', 'wingStopper', 'switchDefender',
      'anchorBig', 'defensiveRebounder', 'rimProtector', 'slowLaterally', 'nonDefender'
    ];
    const defensiveRoles = defensiveRoleKeys.map(key => t(`createPlayer.defensiveRoles.${key}`));

    // Clés de traduction pour mano dominante
    const manoDominanteKeys = ['rightHand', 'leftHand', 'bothHands'];
    const manoDominanteOptions = manoDominanteKeys.map(key => t(`createPlayer.fields.${key}`));

    // Clés de traduction pour catégorie
    const categoriaKeys = ['handler', 'exterior', 'interior'];
    const categoriaOptions = categoriaKeys.map(key => t(`createPlayer.categories.${key}`));
    const shootingRangeOptions = ['Rim', 'Mid-Range', '3Pts'];
    const flagBehaviourOptions = ['Green', 'Orange', 'Red'];
    const injuryProneOptions = ['Green', 'Orange', 'Red'];
    // Clés de traduction pour VBC ratings
    const vbcRatingKeys = [
      'lowLevelProspect', 'midLevelProspect', 'goodLevelProspect', 'topProspect', 'futureStar',
      'ebaLow', 'ebaHigh', 'lebLow', 'lebHigh', 'acbRotation',
      'bottomBclEurocup', 'midBclEurocup', 'topBclEurocup', 'elMid', 'elTop',
      'nbaRotation', 'nbaAllStar', 'collegeLow', 'collegeMid', 'collegeTop'
    ];
    const vbcRatings = vbcRatingKeys.map(key => t(`createPlayer.levels.${key}`));
    const origenEtnicoOptions = [
      'Africanos (subsahariana) - Camerun, Nigeria, Senegal, Mali, etc.',
      'Afrocaribenos - Martinica, Republica Dominicana, Puerto Rico, Islas Virgenes, etc.',
      'Balcanes - Serbia, Croacia, Bosnia, Montenegro, Macedonia del Norte, etc.',
      'Europa Occidental - Francia, Alemania, Espana, Italia, Belgica, etc.',
      'Latinoamericanos - Argentina, Brasil, Venezuela, Mexico, etc.',
      'Nordafricanos - Tunez, Marruecos, Egipto, etc.',
      'Norteamericanos - Estados Unidos, Canada, etc.'
    ];
    const draftPositions = [
      ...Array(30).fill(null).map((_, i) => `1st Round, #${i + 1}`),
      ...Array(30).fill(null).map((_, i) => `2nd Round, #${i + 31}`)
    ];
    // Clés de traduction pour options booléennes
    const booleanExperienceKeys = ['yes', 'no'];
    const booleanExperienceOptions = booleanExperienceKeys.map(key => t(`common.${key}`));
    const priceRanges = [
      '0-50k€', '50-100k€', '100-150k€', '150-200k€', '200-250k€', '250-300k€', '300-350k€',
      '350-400k€', '400-450k€', '450-500k€', '500-550k€', '550-600k€', '600-650k€', '650-700k€',
      '700-750k€', '750-800k€', '800-850k€', '850-900k€', '900-950k€', '950k€-1M€', '1M€-1.2M€',
      '1.2M€-1.4M€', '1.4M€-1.6M€', '1.6M€-1.8M€', '1.8M€-2M€', '2M€-2.2M€', '2.2M€-2.4M€', '2.4M€-2.5M€'
    ];
    const contratos = [
      '2-Way', 'Exhibit-10', '1 año', '1+1', '2 años', '3 años', 'On Loan'
    ];
    const licenciaOptions = ['EXT', 'EUR', 'JFL', 'COT'];
    // Clés de traduction pour JE Attributes
    const JEAttributeKeys = [
      'oneOnOne', 'threeLevelScorer', 'offBallActivity', 'athletic',
      'pickAndRollAbility', 'foulDrawing', 'clutch', 'tough', 'energy',
      'wingspan', 'physical', 'hustle', 'bbiq', 'postGame', 'paintTouches',
      'slasher', 'scoring', 'firstStep', 'consistentProduction', 'rebounding',
      'talent', 'shooter', 'catchAndShoot', 'cornerThree', 'pullUpShooting',
      'transition', 'blueCollar', 'speedAgility', 'versatility', 'verticality'
    ];
    const JEAttributes = JEAttributeKeys.map(key => t(`createPlayer.jeAttributes.${key}`));

    // Clés de traduction pour JI Attributes
    const JIAttributeKeys = [
      'oneOnOne', 'offBallActivity', 'athletic', 'screenAndRoll',
      'effectiveScreens', 'softTouch', 'clutch', 'tough', 'energy',
      'wingspan', 'footwork', 'physical', 'hustle', 'bbiq', 'postUpFaceUp',
      'lobThreat', 'scoring', 'consistentProduction', 'rimProtection',
      'rebounding', 'rimRunner', 'talent', 'shooter', 'catchAndShoot',
      'cornerThreePopOut', 'pullUpShooting', 'blueCollar', 'transition',
      'multipleEfforts', 'speedAgility', 'versatility', 'verticality'
    ];
    const JIAttributes = JIAttributeKeys.map(key => t(`createPlayer.jiAttributes.${key}`));

    // Initialiser tous les compteurs
    const offensiveCounts = {};
    const defensiveCounts = {};
    const manoDominanteCounts = {};
    const categoriaCounts = {};
    const shootingRangeCounts = {};
    const currentVBCRatingCounts = {};
    const proyeccionVBCRatingCounts = {};
    const flagBehaviourCounts = {};
    const injuryProneCounts = {};
    const digitalBehaviourCounts = {};
    const origenEtnicoCounts = {};
    const competicionCounts = {};
    const posicionDraftCounts = {};
    const nbaExperienceCounts = {};
    const experienciaAcbCounts = {};
    const experienciaEuropaCounts = {};
    const experienciaFueraCounts = {};
    const experienciaNcaaCounts = {};
    const rangoPreciosCounts = {};
    const contratoCounts = {};
    const buyoutCounts = {};
    const licenciaCounts = {};
    const agenciaCounts = {};
    const leagueCounts = {};
    const competitionCounts = {};
    const jeAttributeCounts = {};
    const jiAttributeCounts = {};
    const nacionalidadCounts = {};
    const segundaNacionalidadCounts = {};
    const seleccionNacionalCounts = {};

    // Initialiser à 0
    offensiveRoles.forEach(role => offensiveCounts[role] = 0);
    defensiveRoles.forEach(role => defensiveCounts[role] = 0);
    manoDominanteOptions.forEach(opt => manoDominanteCounts[opt] = 0);
    categoriaOptions.forEach(opt => categoriaCounts[opt] = 0);
    shootingRangeOptions.forEach(opt => shootingRangeCounts[opt] = 0);
    vbcRatings.forEach(rating => {
      currentVBCRatingCounts[rating] = 0;
      proyeccionVBCRatingCounts[rating] = 0;
    });
    flagBehaviourOptions.forEach(opt => flagBehaviourCounts[opt] = 0);
    injuryProneOptions.forEach(opt => injuryProneCounts[opt] = 0);
    injuryProneOptions.forEach(opt => digitalBehaviourCounts[opt] = 0);
    origenEtnicoOptions.forEach(opt => origenEtnicoCounts[opt] = 0);
    draftPositions.forEach(pos => posicionDraftCounts[pos] = 0);
    booleanExperienceOptions.forEach(opt => {
      nbaExperienceCounts[opt] = 0;
      experienciaAcbCounts[opt] = 0;
      experienciaEuropaCounts[opt] = 0;
      experienciaFueraCounts[opt] = 0;
      experienciaNcaaCounts[opt] = 0;
    });
    priceRanges.forEach(range => {
      rangoPreciosCounts[range] = 0;
      buyoutCounts[range] = 0;
    });
    contratos.forEach(contrato => contratoCounts[contrato] = 0);
    licenciaOptions.forEach(lic => licenciaCounts[lic] = 0);
    JEAttributes.forEach(attr => jeAttributeCounts[attr] = 0);
    JIAttributes.forEach(attr => jiAttributeCounts[attr] = 0);

    // Fonction pour vérifier si un joueur correspond aux filtres actuels de caractéristiques
    const playerMatchesCurrentFilters = (player) => {
      // Appliquer tous les filtres de caractéristiques sauf celui qu'on est en train de compter
      const rolOfensivoMatches =
        rolOfensivoFilter.length === 0 ||
        (player.rolOfensivo &&
          rolOfensivoFilter.some((role) =>
            normalizeRoleValue(player.rolOfensivo).split(',').map((r) => t(`createPlayer.offensiveRoles.${r.trim()}`, r.trim())).includes(role)
          ));

      const rolDefensivoMatches =
        rolDefensivoFilter.length === 0 ||
        (player.rolDefensivo &&
          rolDefensivoFilter.some((role) =>
            normalizeRoleValue(player.rolDefensivo).split(',').map((r) => t(`createPlayer.defensiveRoles.${r.trim()}`, r.trim())).includes(role)
          ));

      const manoDominanteMatches =
        manoDominanteFilter.length === 0 ||
        manoDominanteFilter.includes(player.manoDominante);

      const categoriaMatches =
        categoriaFilter.length === 0 ||
        categoriaFilter.includes(player.categoria);

      const shootingRangeMatches =
        shootingRangeFilter.length === 0 ||
        shootingRangeFilter.includes(player.shootingRange);

      const jeJiTypeMatches =
        !jeJiTypeFilter || player.jeJiType === jeJiTypeFilter;

      const jeJiAttributesMatches =
        jeJiAttributesFilter.length === 0 ||
        (player.jeJiAttributes &&
          jeJiAttributesFilter.some((attr) =>
            player.jeJiAttributes.includes(attr)
          ));

      const currentVBCRatingMatches =
        currentVBCRatingFilter.length === 0 ||
        currentVBCRatingFilter.includes(player.currentVBCRating);

      const proyeccionVBCRatingMatches =
        proyeccionVBCRatingFilter.length === 0 ||
        proyeccionVBCRatingFilter.includes(player.proyeccionVBCRating);

      const flagBehaviourMatches =
        flagBehaviourFilter.length === 0 ||
        flagBehaviourFilter.includes(player.flagBehaviour);

      const injuryProneMatches =
        injuryProneFilter.length === 0 ||
        injuryProneFilter.includes(player.injuryProne);

      const digitalBehaviourMatches =
        digitalBehaviourFilter.length === 0 ||
        digitalBehaviourFilter.includes(player.digitalBehaviour);

      const comparacionProMatches =
        !comparacionProFilter ||
        (player.comparacionPro &&
          player.comparacionPro
            .toLowerCase()
            .includes(comparacionProFilter.toLowerCase()));

      const nacionalidadMatches =
        !nacionalidadFilter || player.nacionalidad === nacionalidadFilter;

      const segundaNacionalidadMatches =
        !segundaNacionalidadFilter ||
        player.segundaNacionalidad === segundaNacionalidadFilter;

      const origenEtnicoMatches =
        origenEtnicoFilter.length === 0 ||
        origenEtnicoFilter.includes(player.origenEtnico);

      const seleccionNacionalMatches =
        !seleccionNacionalFilter ||
        (player.seleccionNacional &&
          player.seleccionNacional === seleccionNacionalFilter);

      // Vérifier UNIQUEMENT les équipes des nouvelles tables
      const equipoActualMatches = equipoActualFilter.length === 0 || (
        player.competitions_detail && player.competitions_detail.length > 0 &&
        player.competitions_detail.some(c => equipoActualFilter.includes(c.equipe_nom))
      );

      // Vérifier UNIQUEMENT les compétitions des nouvelles tables
      const competicionMatches = competicionFilter.length === 0 || (
        player.competitions_detail && player.competitions_detail.length > 0 &&
        player.competitions_detail.some(c => competicionFilter.includes(c.competition_nom))
      );

      const anioDraftMatches =
        !añoDraftFilter ||
        (player.anioDraft && player.anioDraft.toString().includes(añoDraftFilter));

      const posicionDraftMatches =
        posicionDraftFilter.length === 0 ||
        posicionDraftFilter.includes(player.posicionDraft);

      const equipoPreDraftMatches =
        !equipoPreDraftFilter ||
        (player.equipoPreDraft &&
          player.equipoPreDraft
            .toLowerCase()
            .includes(equipoPreDraftFilter.toLowerCase()));

      const pitMatches =
        !pitFilter || (player.pit && player.pit.toString().includes(pitFilter));

      const eurocampMatches =
        !eurocampFilter ||
        (player.eurocamp && player.eurocamp.toString().includes(eurocampFilter));

      const slvegasMatches =
        !slvegasFilter ||
        (player.slvegas && player.slvegas.toString().includes(slvegasFilter));

      const nbaExperienceMatches =
        nbaExperienceFilter.length === 0 ||
        nbaExperienceFilter.includes(player.nbaExperience);

      const experienciaAcbMatches =
        experienciaAcbFilter.length === 0 ||
        experienciaAcbFilter.includes(player.experienciaAcb);

      const experiencaEuropaMatches =
        experiencaEuropaFilter.length === 0 ||
        experiencaEuropaFilter.includes(player.experienciaEuropa);

      const experienca_fueraMatches =
        experienca_fueraFilter.length === 0 ||
        experienca_fueraFilter.includes(player.experienciaFuera);

      const experienca_ncaaMatches =
        experienca_ncaaFilter.length === 0 ||
        experienca_ncaaFilter.includes(player.experienciaNcaa);

      const rangoPreciosMatches =
        rangoPreciosFilter.length === 0 ||
        (player.rangoPrecios && rangoPreciosFilter.includes(player.rangoPrecios));

      const contratoMatches =
        contratoFilter.length === 0 ||
        (player.contrato && contratoFilter.includes(player.contrato));

      const buyoutMatches =
        buyoutFilter.length === 0 ||
        (player.buyout && buyoutFilter.includes(player.buyout));

      return (
        rolOfensivoMatches &&
        rolDefensivoMatches &&
        manoDominanteMatches &&
        categoriaMatches &&
        shootingRangeMatches &&
        jeJiTypeMatches &&
        jeJiAttributesMatches &&
        currentVBCRatingMatches &&
        proyeccionVBCRatingMatches &&
        flagBehaviourMatches &&
        injuryProneMatches &&
        digitalBehaviourMatches &&
        comparacionProMatches &&
        nacionalidadMatches &&
        segundaNacionalidadMatches &&
        origenEtnicoMatches &&
        seleccionNacionalMatches &&
        equipoActualMatches &&
        competicionMatches &&
        anioDraftMatches &&
        posicionDraftMatches &&
        equipoPreDraftMatches &&
        pitMatches &&
        eurocampMatches &&
        slvegasMatches &&
        nbaExperienceMatches &&
        experienciaAcbMatches &&
        experiencaEuropaMatches &&
        experienca_fueraMatches &&
        experienca_ncaaMatches &&
        rangoPreciosMatches &&
        contratoMatches &&
        buyoutMatches
      );
    };

    // Compter les joueurs pour chaque attribut
    playersForCounting.forEach(player => {
      // Ne compter que si le joueur correspond aux autres filtres de caractéristiques
      if (!playerMatchesCurrentFilters(player)) return;
      // Rôles offensifs
      if (player.rolOfensivo) {
        const roles = normalizeRoleValue(player.rolOfensivo).split(',').map(r => r.trim());
        roles.forEach(role => {
          const translated = t(`createPlayer.offensiveRoles.${role}`, role);
          if (offensiveCounts.hasOwnProperty(translated)) {
            offensiveCounts[translated]++;
          }
        });
      }

      // Rôles défensifs
      if (player.rolDefensivo) {
        const roles = normalizeRoleValue(player.rolDefensivo).split(',').map(r => r.trim());
        roles.forEach(role => {
          const translated = t(`createPlayer.defensiveRoles.${role}`, role);
          if (defensiveCounts.hasOwnProperty(translated)) {
            defensiveCounts[translated]++;
          }
        });
      }

      // Mano dominante
      if (player.manoDominante && manoDominanteCounts.hasOwnProperty(player.manoDominante)) {
        manoDominanteCounts[player.manoDominante]++;
      }

      // Categoría
      if (player.categoria && categoriaCounts.hasOwnProperty(player.categoria)) {
        categoriaCounts[player.categoria]++;
      }

      // Shooting range
      if (player.shootingRange && shootingRangeCounts.hasOwnProperty(player.shootingRange)) {
        shootingRangeCounts[player.shootingRange]++;
      }

      // Current VBC Rating
      if (player.currentVBCRating && currentVBCRatingCounts.hasOwnProperty(player.currentVBCRating)) {
        currentVBCRatingCounts[player.currentVBCRating]++;
      }

      // Proyección VBC Rating
      if (player.proyeccionVBCRating && proyeccionVBCRatingCounts.hasOwnProperty(player.proyeccionVBCRating)) {
        proyeccionVBCRatingCounts[player.proyeccionVBCRating]++;
      }

      // Flag Behaviour
      if (player.flagBehaviour && flagBehaviourCounts.hasOwnProperty(player.flagBehaviour)) {
        flagBehaviourCounts[player.flagBehaviour]++;
      }

      // Injury Prone
      if (player.injuryProne && injuryProneCounts.hasOwnProperty(player.injuryProne)) {
        injuryProneCounts[player.injuryProne]++;
      }

      // Digital Behaviour
      if (player.digitalBehaviour && digitalBehaviourCounts.hasOwnProperty(player.digitalBehaviour)) {
        digitalBehaviourCounts[player.digitalBehaviour]++;
      }

      // Origen Étnico
      if (player.origenEtnico && origenEtnicoCounts.hasOwnProperty(player.origenEtnico)) {
        origenEtnicoCounts[player.origenEtnico]++;
      }

      // Compétitions (nouvelles et anciennes)
      if (player.competitions_detail && player.competitions_detail.length > 0) {
        player.competitions_detail.forEach(c => {
          if (c.competition_nom) {
            if (!competicionCounts[c.competition_nom]) {
              competicionCounts[c.competition_nom] = 0;
            }
            competicionCounts[c.competition_nom]++;
          }
        });
      }
      // Anciennes compétitions pour rétrocompatibilité
      ['league_2'].forEach(field => {
        if (player[field] && player[field] !== 'null') {
          if (!competicionCounts[player[field]]) {
            competicionCounts[player[field]] = 0;
          }
          competicionCounts[player[field]]++;
        }
      });

      // Posición Draft
      if (player.posicionDraft && posicionDraftCounts.hasOwnProperty(player.posicionDraft)) {
        posicionDraftCounts[player.posicionDraft]++;
      }

      // Experiencias
      // Debug: vérifier les valeurs
      if (player.nbaExperience) {
        console.log('DEBUG nbaExperience:', player.nbaExperience, 'keys:', Object.keys(nbaExperienceCounts), 'hasOwn:', nbaExperienceCounts.hasOwnProperty(player.nbaExperience));
      }
      if (player.nbaExperience && nbaExperienceCounts.hasOwnProperty(player.nbaExperience)) {
        nbaExperienceCounts[player.nbaExperience]++;
      }
      if (player.experienciaAcb && experienciaAcbCounts.hasOwnProperty(player.experienciaAcb)) {
        experienciaAcbCounts[player.experienciaAcb]++;
      }
      if (player.experienciaEuropa && experienciaEuropaCounts.hasOwnProperty(player.experienciaEuropa)) {
        experienciaEuropaCounts[player.experienciaEuropa]++;
      }
      if (player.experienciaFuera && experienciaFueraCounts.hasOwnProperty(player.experienciaFuera)) {
        experienciaFueraCounts[player.experienciaFuera]++;
      }
      if (player.experienciaNcaa && experienciaNcaaCounts.hasOwnProperty(player.experienciaNcaa)) {
        experienciaNcaaCounts[player.experienciaNcaa]++;
      }

      // Rango precios
      if (player.rangoPrecios && rangoPreciosCounts.hasOwnProperty(player.rangoPrecios)) {
        rangoPreciosCounts[player.rangoPrecios]++;
      }

      // Contrato
      if (player.contrato && contratoCounts.hasOwnProperty(player.contrato)) {
        contratoCounts[player.contrato]++;
      }

      // Buyout
      if (player.buyout && buyoutCounts.hasOwnProperty(player.buyout)) {
        buyoutCounts[player.buyout]++;
      }

      // Licencia
      if (player.pasaporte && licenciaCounts.hasOwnProperty(player.pasaporte)) {
        licenciaCounts[player.pasaporte]++;
      }

      // Agencia
      if (player.agencia) {
        if (!agenciaCounts.hasOwnProperty(player.agencia)) {
          agenciaCounts[player.agencia] = 0;
        }
        agenciaCounts[player.agencia]++;
      }

      // Skills JE/JI attributes
      if (player.jeJiType === 'JE' && player.jeJiAttributes) {
        // Compter les attributs JE
        const attrs = player.jeJiAttributes.split(',').map(a => a.trim());
        attrs.forEach(attr => {
          if (jeAttributeCounts.hasOwnProperty(attr)) {
            jeAttributeCounts[attr]++;
          }
        });
      } else if (player.jeJiType === 'JI' && player.jeJiAttributes) {
        // Compter les attributs JI
        const attrs = player.jeJiAttributes.split(',').map(a => a.trim());
        attrs.forEach(attr => {
          if (jiAttributeCounts.hasOwnProperty(attr)) {
            jiAttributeCounts[attr]++;
          }
        });
      }

      // Nacionalidad
      if (player.nacionalidad) {
        if (!nacionalidadCounts.hasOwnProperty(player.nacionalidad)) {
          nacionalidadCounts[player.nacionalidad] = 0;
        }
        nacionalidadCounts[player.nacionalidad]++;
      }

      // Segunda nacionalidad
      if (player.segundaNacionalidad) {
        if (!segundaNacionalidadCounts.hasOwnProperty(player.segundaNacionalidad)) {
          segundaNacionalidadCounts[player.segundaNacionalidad] = 0;
        }
        segundaNacionalidadCounts[player.segundaNacionalidad]++;
      }

      // Selección nacional
      if (player.seleccionNacional) {
        if (!seleccionNacionalCounts.hasOwnProperty(player.seleccionNacional)) {
          seleccionNacionalCounts[player.seleccionNacional] = 0;
        }
        seleccionNacionalCounts[player.seleccionNacional]++;
      }
    });

    // Mettre à jour tous les états
    setRolOfensivoCounts(offensiveCounts);
    setRolDefensivoCounts(defensiveCounts);
    setManoDominanteCounts(manoDominanteCounts);
    setCategoriaCounts(categoriaCounts);
    setShootingRangeCounts(shootingRangeCounts);
    setCurrentVBCRatingCounts(currentVBCRatingCounts);
    setProyeccionVBCRatingCounts(proyeccionVBCRatingCounts);
    setFlagBehaviourCounts(flagBehaviourCounts);
    setInjuryProneCounts(injuryProneCounts);
    setDigitalBehaviourCounts(digitalBehaviourCounts);
    setOrigenEtnicoCounts(origenEtnicoCounts);
    setCompeticionCounts(competicionCounts);
    setPosicionDraftCounts(posicionDraftCounts);
    setNbaExperienceCounts(nbaExperienceCounts);
    setExperienciaAcbCounts(experienciaAcbCounts);
    setExperienciaEuropaCounts(experienciaEuropaCounts);
    setExperienciaFueraCounts(experienciaFueraCounts);
    setExperienciaNcaaCounts(experienciaNcaaCounts);
    setRangoPreciosCounts(rangoPreciosCounts);
    setContratoCounts(contratoCounts);
    setBuyoutCounts(buyoutCounts);
    setLicenciaCounts(licenciaCounts);
    setAgenciaCounts(agenciaCounts);
    setLeagueCounts(leagueCounts);
    setCompetitionCounts(competitionCounts);
    setJeAttributeCounts(jeAttributeCounts);
    setJiAttributeCounts(jiAttributeCounts);
    setNacionalidadCounts(nacionalidadCounts);
    setSegundaNacionalidadCounts(segundaNacionalidadCounts);
    setSeleccionNacionalCounts(seleccionNacionalCounts);
  }, [
    players,
    // Filtres de base
    groupFilter,
    positionFilter,
    playerTypeFilter,
    yearFilter,
    nameFilter,
    leagueFilter,
    heightFilter,
    ageFilter,
    licenciaFilter,
    agenciaFilter,
    // Filtres de caractéristiques
    rolOfensivoFilter,
    rolDefensivoFilter,
    manoDominanteFilter,
    shootingRangeFilter,
    jeJiTypeFilter,
    jeJiAttributesFilter,
    currentVBCRatingFilter,
    proyeccionVBCRatingFilter,
    flagBehaviourFilter,
    injuryProneFilter,
    digitalBehaviourFilter,
    comparacionProFilter,
    nacionalidadFilter,
    segundaNacionalidadFilter,
    origenEtnicoFilter,
    seleccionNacionalFilter,
    equipoActualFilter,
    competicionFilter,
    añoDraftFilter,
    posicionDraftFilter,
    equipoPreDraftFilter,
    pitFilter,
    eurocampFilter,
    slvegasFilter,
    nbaExperienceFilter,
    experienciaAcbFilter,
    experiencaEuropaFilter,
    experienca_fueraFilter,
    experienca_ncaaFilter,
    rangoPreciosFilter,
    contratoFilter,
    buyoutFilter
  ]);

  // Réinitialiser la page à 0 quand les filtres changent
  useEffect(() => {
    setPage(0);
  }, [
    nameFilter,
    yearFilter,
    positionFilter,
    groupFilter,
    leagueFilter,
    competitionFilter,
    heightFilter,
    ageFilter,
    playerTypeFilter,
    licenciaFilter,
    agenciaFilter,
    informeFilter,
    scoutFilter,
    verdictFilter,
    competitionDetailFilter,
    rolOfensivoFilter,
    manoDominanteFilter,
    categoriaFilter,
    shootingRangeFilter,
    rolDefensivoFilter,
    jeJiTypeFilter,
    jeJiAttributesFilter,
    currentVBCRatingFilter,
    proyeccionVBCRatingFilter,
    flagBehaviourFilter,
    injuryProneFilter,
    digitalBehaviourFilter,
    comparacionProFilter,
    nacionalidadFilter,
    segundaNacionalidadFilter,
    origenEtnicoFilter,
    seleccionNacionalFilter,
    equipoActualFilter,
    competicionFilter,
    añoDraftFilter,
    posicionDraftFilter,
    equipoPreDraftFilter,
    pitFilter,
    eurocampFilter,
    slvegasFilter,
    nbaExperienceFilter,
    experienciaAcbFilter,
    experiencaEuropaFilter,
    experienca_fueraFilter,
    experienca_ncaaFilter,
    rangoPreciosFilter,
    contratoFilter,
    buyoutFilter,
    alturaFilter,
    alturaZapatosFilter,
    envergaduraFilter,
    alcanceFilter,
    saltoVerticalMaxFilter,
    saltoVerticalPieFilter,
    medidasPesoFilter,
    grasaCorporalFilter,
    longitudManoFilter,
    anchoManoFilter,
    pressBancaFilter,
    agilidadFilter,
    sprintCuartoFilter,
    gamesFilter,
    minutesFilter,
    pointsFilter,
    twoFGPercentFilter,
    threeFGPercentFilter,
    freeThrowPercentFilter,
    rebOffFilter,
    rebDefFilter,
    rebTotFilter,
    assistsFilter,
    foulsFilter,
    blocksFilter,
    stealsFilter,
    turnoversFilter,
    rankingFilter
  ]);

  // -----------------------------------------------------------
  // FILTRAGE FINAL
  // -----------------------------------------------------------
  const filteredPlayers = players.filter((player) => {
    // 1) Calcul d'âge
    const playerAge = calculateAge(player.dateNaissance);

    // 2) Groupes
    const playerGroups = player.groupes.map((g) => g.libelle);
    const hasSinGrupos = groupFilter.includes('Sin Grupos');
    const playerHasNoGroups = !player.groupes || player.groupes.length === 0;
    const groupCondition =
      groupFilter.length === 0
        ? true
        : (hasSinGrupos && playerHasNoGroups) ||
          playerGroups.some((g) => groupFilter.includes(g));

    // 3) Positions
    const positionCondition =
      positionFilter.length === 0 ||
      positionFilter.includes(getPositionKey(player.poste));

    // 4) Type (cantera/pro/NCAA)
    const typeCondition = matchesPlayerType(player, playerTypeFilter, { excludeProspects: excludeProspectsFromPro });

    // 5) Année de naissance
    const yearSearchTerm = yearFilter.toLowerCase();
    const yearCondition =
      !yearFilter || (player.anneeNaissance + '').includes(yearSearchTerm);

    // 6) Nom + team => "search"
    const searchTerm = nameFilter.toLowerCase();
    // Vérifier aussi dans les nouvelles équipes et compétitions
    const hasEquipeMatch = player.competitions_detail && player.competitions_detail.length > 0 &&
      player.competitions_detail.some(c =>
        (c.equipe_nom && c.equipe_nom.toLowerCase().includes(searchTerm)) ||
        (c.competition_nom && c.competition_nom.toLowerCase().includes(searchTerm))
      );

    const nameTeamCondition =
      (!nameFilter && !yearFilter) ||
      (player.nom && player.nom.toLowerCase().includes(searchTerm)) ||
      (player.team && player.team.toLowerCase().includes(searchTerm)) ||
      hasEquipeMatch;

    // 7) Ligue / compétition
    const leagueCondition =
      leagueFilter.length === 0 || leagueFilter.includes(player.league_1);
    const competCondition =
      competitionFilter.length === 0 ||
      competitionFilter.includes(player.competicionEuropa);

    // 8) Taille / Âge
    const heightCondition =
      player.taille >= heightFilter[0] && player.taille <= heightFilter[1];
    const ageCondition = playerAge >= ageFilter[0] && playerAge <= ageFilter[1];

    // 9) Licencia / Agencia
    const licenciaCondition =
      licenciaFilter.length === 0 || licenciaFilter.includes(player.pasaporte);
    const agenciaCondition =
      agenciaFilter.length === 0 || agenciaFilter.includes(player.agencia);

    // 10) Caractéristiques
    const rolOfensivoMatches =
      rolOfensivoFilter.length === 0 ||
      (player.rolOfensivo &&
        rolOfensivoFilter.some((role) =>
          normalizeRoleValue(player.rolOfensivo).split(',').map((r) => t(`createPlayer.offensiveRoles.${r.trim()}`, r.trim())).includes(role)
        ));

    const rolDefensivoMatches =
      rolDefensivoFilter.length === 0 ||
      (player.rolDefensivo &&
        rolDefensivoFilter.some((role) =>
          normalizeRoleValue(player.rolDefensivo).split(',').map((r) => t(`createPlayer.defensiveRoles.${r.trim()}`, r.trim())).includes(role)
        ));

    const manoDominanteMatches =
      manoDominanteFilter.length === 0 ||
      manoDominanteFilter.includes(player.manoDominante);

    const categoriaMatches =
      categoriaFilter.length === 0 ||
      categoriaFilter.includes(player.categoria);

    const shootingRangeMatches =
      shootingRangeFilter.length === 0 ||
      shootingRangeFilter.includes(player.shootingRange);

    const jeJiTypeMatches =
      !jeJiTypeFilter || player.jeJiType === jeJiTypeFilter;

    const jeJiAttributesMatches =
      jeJiAttributesFilter.length === 0 ||
      (player.jeJiAttributes &&
        jeJiAttributesFilter.some((attr) =>
          player.jeJiAttributes.includes(attr)
        ));

    const currentVBCRatingMatches =
      currentVBCRatingFilter.length === 0 ||
      currentVBCRatingFilter.includes(player.currentVBCRating);

    const proyeccionVBCRatingMatches =
      proyeccionVBCRatingFilter.length === 0 ||
      proyeccionVBCRatingFilter.includes(player.proyeccionVBCRating);

    const flagBehaviourMatches =
      flagBehaviourFilter.length === 0 ||
      flagBehaviourFilter.includes(player.flagBehaviour);

    const injuryProneMatches =
      injuryProneFilter.length === 0 ||
      injuryProneFilter.includes(player.injuryProne);

    const digitalBehaviourMatches =
      digitalBehaviourFilter.length === 0 ||
      digitalBehaviourFilter.includes(player.digitalBehaviour);

    const comparacionProMatches =
      !comparacionProFilter ||
      (player.comparacionPro &&
        player.comparacionPro
          .toLowerCase()
          .includes(comparacionProFilter.toLowerCase()));

    // 11) Nationalités
    const nacionalidadMatches =
      !nacionalidadFilter || player.nacionalidad === nacionalidadFilter;
    const segundaNacionalidadMatches =
      !segundaNacionalidadFilter ||
      player.segundaNacionalidad === segundaNacionalidadFilter;
    const origenEtnicoMatches =
      origenEtnicoFilter.length === 0 ||
      origenEtnicoFilter.includes(player.origenEtnico);

    // 12) Situation
    const seleccionNacionalMatches =
      !seleccionNacionalFilter ||
      (player.seleccionNacional &&
        player.seleccionNacional === seleccionNacionalFilter);

    // Vérifier UNIQUEMENT les équipes des nouvelles tables
    const equipoActualMatches = equipoActualFilter.length === 0 || (
      player.competitions_detail && player.competitions_detail.length > 0 &&
      player.competitions_detail.some(c => equipoActualFilter.includes(c.equipe_nom))
    );

    // Vérifier UNIQUEMENT les compétitions des nouvelles tables
    const competicionMatches = competicionFilter.length === 0 || (
      player.competitions_detail && player.competitions_detail.length > 0 &&
      player.competitions_detail.some(c => competicionFilter.includes(c.competition_nom))
    );

    // 13) Expérience
    const anioDraftMatches =
      !añoDraftFilter ||
      (player.anioDraft && player.anioDraft.toString().includes(añoDraftFilter));

    const posicionDraftMatches =
      posicionDraftFilter.length === 0 ||
      posicionDraftFilter.includes(player.posicionDraft);

    const equipoPreDraftMatches =
      !equipoPreDraftFilter ||
      (player.equipoPreDraft &&
        player.equipoPreDraft
          .toLowerCase()
          .includes(equipoPreDraftFilter.toLowerCase()));

    const pitMatches =
      !pitFilter || (player.pit && player.pit.toString().includes(pitFilter));

    const eurocampMatches =
      !eurocampFilter ||
      (player.eurocamp && player.eurocamp.toString().includes(eurocampFilter));

    const slvegasMatches =
      !slvegasFilter ||
      (player.slvegas && player.slvegas.toString().includes(slvegasFilter));

    const nbaExperienceMatches =
      nbaExperienceFilter.length === 0 ||
      nbaExperienceFilter.includes(player.nbaExperience);

    const experienciaAcbMatches =
      experienciaAcbFilter.length === 0 ||
      experienciaAcbFilter.includes(player.experienciaAcb);

    const experiencaEuropaMatches =
      experiencaEuropaFilter.length === 0 ||
      experiencaEuropaFilter.includes(player.experienciaEuropa);

    const experienca_fueraMatches =
      experienca_fueraFilter.length === 0 ||
      experienca_fueraFilter.includes(player.experienciaFuera);

    const experienca_ncaaMatches =
      experienca_ncaaFilter.length === 0 ||
      experienca_ncaaFilter.includes(player.experienciaNcaa);

    // 14) Contrat
    const rangoPreciosMatches =
      rangoPreciosFilter.length === 0 ||
      (player.rangoPrecios && rangoPreciosFilter.includes(player.rangoPrecios));

    const contratoMatches =
      contratoFilter.length === 0 ||
      (player.contrato && contratoFilter.includes(player.contrato));

    const buyoutMatches =
      buyoutFilter.length === 0 ||
      (player.buyout && buyoutFilter.includes(player.buyout));

// 15) Filtres de mesures (>= la valeur)
// CORRECTION : Prendre la mesure la plus récente du joueur
const getLatestMeasure = (playerMedidas) => {
  if (!playerMedidas || playerMedidas.length === 0) return null;
  
  // Trier par date décroissante et prendre la première (la plus récente)
  const sortedMedidas = [...playerMedidas].sort((a, b) => {
    const dateA = new Date(a.fecha);
    const dateB = new Date(b.fecha);
    return dateB - dateA; // ordre décroissant
  });
  
  return sortedMedidas[0];
};

const checkMeasureFilter = (playerMedidas, filterValue, measureName) => {
  if (!filterValue) return true;
  if (!playerMedidas || playerMedidas.length === 0) return false;
  
  const numericFilter = parseFloat(filterValue);
  if (isNaN(numericFilter)) return true;
  
  // Prendre la mesure la plus récente
  const latestMeasure = getLatestMeasure(playerMedidas);
  if (!latestMeasure) return false;
  
  const measureValue = parseFloat(latestMeasure[measureName]) || 0;
  return measureValue >= numericFilter;
};

const competitionDetailCondition = 
  !competitionDetailFilter ? true :
  competitionDetailFilter === 'with' ? 
    (player.competitions_detail && player.competitions_detail.length > 0) :
  competitionDetailFilter === 'without' ? 
    (!player.competitions_detail || player.competitions_detail.length === 0) :
  true;

// Application des filtres de mesures 
const alturaCondition = checkMeasureFilter(player.medidas, alturaFilter, 'altura');
const alturaZapatosCondition = checkMeasureFilter(player.medidas, alturaZapatosFilter, 'altura_con_zapatos');
const envergaduraCondition = checkMeasureFilter(player.medidas, envergaduraFilter, 'envergadura');
const alcanceCondition = checkMeasureFilter(player.medidas, alcanceFilter, 'alcance');
const saltoVerticalMaxCondition = checkMeasureFilter(player.medidas, saltoVerticalMaxFilter, 'salto_vertical_maximo');
const saltoVerticalPieCondition = checkMeasureFilter(player.medidas, saltoVerticalPieFilter, 'salto_vertical_de_pie');
const medidasPesoCondition = checkMeasureFilter(player.medidas, medidasPesoFilter, 'peso');
const grasaCorporalCondition = checkMeasureFilter(player.medidas, grasaCorporalFilter, 'grasa_corporal');
const longitudManoCondition = checkMeasureFilter(player.medidas, longitudManoFilter, 'longitud_mano');
const anchoManoCondition = checkMeasureFilter(player.medidas, anchoManoFilter, 'ancho_mano');
const pressBancaCondition = checkMeasureFilter(player.medidas, pressBancaFilter, 'press_banca');
const agilidadCondition = checkMeasureFilter(player.medidas, agilidadFilter, 'agilidad');
const sprintCuartoCondition = checkMeasureFilter(player.medidas, sprintCuartoFilter, 'sprint_cuarto');


    const informeCondition =
  !informeFilter ? true :
  informeFilter === 'zero' ? (player.nb_informe === 0) :
  informeFilter === 'red' ? (player.nb_informe === 0 || player.nb_informe === 1) :
  informeFilter === 'green' ? (player.nb_informe > 1) :
  true;

const scoutCondition = 
  !scoutFilter ? true :
  scoutFilter === 'red' ? (player.nb_scout === 0 || player.nb_scout === 1) :
  scoutFilter === 'green' ? (player.nb_scout > 1) :
  true;

    // 16) Filtres "estadisticas" PAR COMPÉTITION (>=)
    // On vérifie s'il y a au moins UNE compétition 
    // où la stat est >= la valeur entrée.
    const playerStats = player.stats || [];

    const statsGamesCondition = checkStatFilter(
      playerStats,
      gamesFilter,
      'games'
    );
    const statsMinutesCondition = checkStatFilter(
      playerStats,
      minutesFilter,
      'minutes'
    );
    const statsPointsCondition = checkStatFilter(
      playerStats,
      pointsFilter,
      'points'
    );
    const statsTwoFGCondition = checkStatFilter(
      playerStats,
      twoFGPercentFilter,
      'two_fg_percent'
    );
    const statsThreeFGCondition = checkStatFilter(
      playerStats,
      threeFGPercentFilter,
      'three_fg_percent'
    );
    const statsFTCondition = checkStatFilter(
      playerStats,
      freeThrowPercentFilter,
      'free_throw_percent'
    );
    const statsRebOffCondition = checkStatFilter(
      playerStats,
      rebOffFilter,
      'rebounds_off'
    );
    const statsRebDefCondition = checkStatFilter(
      playerStats,
      rebDefFilter,
      'rebounds_def'
    );
    const statsRebTotCondition = checkStatFilter(
      playerStats,
      rebTotFilter,
      'total_rebounds'
    );
    const statsAssistsCondition = checkStatFilter(
      playerStats,
      assistsFilter,
      'assists'
    );
    const statsFoulsCondition = checkStatFilter(
      playerStats,
      foulsFilter,
      'personal_fouls'
    );
    const statsBlocksCondition = checkStatFilter(
      playerStats,
      blocksFilter,
      'blocks'
    );
    const statsStealsCondition = checkStatFilter(
      playerStats,
      stealsFilter,
      'steals'
    );
    const statsTurnoversCondition = checkStatFilter(
      playerStats,
      turnoversFilter,
      'turnovers'
    );
    const statsRankingCondition = checkStatFilter(
      playerStats,
      rankingFilter,
      'ranking'
    );

    const verdictCondition = 
  verdictFilter.length === 0 || 
  (player.scout_verdicts && player.scout_verdicts.some(sv => 
    verdictFilter.includes(sv.verdict)
  ));

    const statsCondition =
      statsGamesCondition &&
      statsMinutesCondition &&
      statsPointsCondition &&
      statsTwoFGCondition &&
      statsThreeFGCondition &&
      statsFTCondition &&
      statsRebOffCondition &&
      statsRebDefCondition &&
      statsRebTotCondition &&
      statsAssistsCondition &&
      statsFoulsCondition &&
      statsBlocksCondition &&
      statsStealsCondition &&
      statsTurnoversCondition &&
      statsRankingCondition;

    // Condition globale
    return (
      nameTeamCondition &&
      groupCondition &&
      positionCondition &&
      typeCondition &&
      yearCondition &&
      leagueCondition &&
      competCondition &&
      heightCondition &&
      competitionDetailCondition &&
      ageCondition &&
      licenciaCondition &&
      agenciaCondition &&
      informeCondition &&
      scoutCondition &&
      verdictCondition &&
      // Caract
      rolOfensivoMatches &&
      rolDefensivoMatches &&
      manoDominanteMatches &&
      categoriaMatches &&
      shootingRangeMatches &&
      jeJiTypeMatches &&
      jeJiAttributesMatches &&
      currentVBCRatingMatches &&
      proyeccionVBCRatingMatches &&
      flagBehaviourMatches &&
      injuryProneMatches &&
      digitalBehaviourMatches &&
      comparacionProMatches &&
      // Nationalités
      nacionalidadMatches &&
      segundaNacionalidadMatches &&
      origenEtnicoMatches &&
      // Situation
      seleccionNacionalMatches &&
      equipoActualMatches &&
      competicionMatches &&
      // Expérience
      anioDraftMatches &&
      posicionDraftMatches &&
      equipoPreDraftMatches &&
      pitMatches &&
      eurocampMatches &&
      slvegasMatches &&
      nbaExperienceMatches &&
      experienciaAcbMatches &&
      experiencaEuropaMatches &&
      experienca_fueraMatches &&
      experienca_ncaaMatches &&
      // Contrat
      rangoPreciosMatches &&
      contratoMatches &&
      buyoutMatches &&
      // Mesures
      alturaCondition &&
      alturaZapatosCondition &&
      envergaduraCondition &&
      alcanceCondition &&
      saltoVerticalMaxCondition &&
      saltoVerticalPieCondition &&
      medidasPesoCondition &&
      grasaCorporalCondition &&
      longitudManoCondition &&
      anchoManoCondition &&
      pressBancaCondition &&
      agilidadCondition &&
      sprintCuartoCondition &&
      // Stats
      statsCondition
    );
  });

  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sélection multiple
  const handleSelectPlayer = (playerId) => {
    setSelectedPlayerIDs((prev) =>
      prev.includes(playerId) ? prev.filter((id) => id !== playerId) : [...prev, playerId]
    );
  };
  const handleSelectAll = (checked) => {
    if (checked) {
      const allIDs = filteredPlayers.map((p) => p.id);
      setSelectedPlayerIDs(allIDs);
    } else {
      setSelectedPlayerIDs([]);
    }
  };

  // Suppression simple
  const handleDeleteClick = (player, e) => {
    e.stopPropagation();
    setPlayerToDelete(player);
    setDeleteDialogOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (!playerToDelete) return;
    try {
      await api.delete(`/api/players/${playerToDelete.id}`);
      setPlayers((prev) => prev.filter((p) => p.id !== playerToDelete.id));
      setPlayerToDelete(null);
    } catch (error) {
      console.error('Error al eliminar el jugador:', error);
    } finally {
      setDeleteDialogOpen(false);
    }
  };
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPlayerToDelete(null);
  };

  // Suppression multiple
  const handleOpenMultiDeleteDialog = () => {
    setMultiDeleteDialogOpen(true);
  };
  const handleCloseMultiDeleteDialog = () => {
    setMultiDeleteDialogOpen(false);
  };
  const handleConfirmMultipleDelete = async () => {
    try {
      for (const id of selectedPlayerIDs) {
        await api.delete(`/api/players/${id}`);
      }
      setPlayers((prev) => prev.filter((p) => !selectedPlayerIDs.includes(p.id)));
      setSelectedPlayerIDs([]);
    } catch (error) {
      console.error('Error al eliminar varios jugadores:', error);
    } finally {
      setMultiDeleteDialogOpen(false);
    }
  };

  // Vue détaillée
  const handleViewPlayer = (player) => {
    if (isMobile) {
      navigate(`/players/${player.id}`);
    } else {
      window.open(`/players/${player.id}`, '_blank');
    }
  };

  // Onglets
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Gestion des onglets de filtres (NOUVEAU)
  const handleFilterTabChange = (event, newValue) => {
    setFilterTab(newValue);
  };

  // Fonction pour réinitialiser tous les filtres
  const handleResetAllFilters = () => {
    // Filtres basiques
    setNameFilter('');
    setYearFilter('');
    setPositionFilter([]);
    setGroupFilter([]);
    setLeagueFilter([]);
    setCompetitionFilter([]);
    setHeightFilter([100, 230]);
    setAgeFilter([8, 42]);
    setPlayerTypeFilter(PLAYER_TYPE.ALL);
    setLicenciaFilter([]);
    setAgenciaFilter([]);
    setInformeFilter('');
    setScoutFilter('');
    setVerdictFilter([]);
    setCompetitionDetailFilter(null);

    // Filtres caractéristiques
    setRolOfensivoFilter([]);
    setManoDominanteFilter([]);
    setCategoriaFilter([]);
    setShootingRangeFilter([]);
    setRolDefensivoFilter([]);
    setJeJiTypeFilter('');
    setJeJiAttributesFilter([]);
    setCurrentVBCRatingFilter([]);
    setProyeccionVBCRatingFilter([]);
    setFlagBehaviourFilter([]);
    setInjuryProneFilter([]);
    setComparacionProFilter('');

    // Filtres nationalité
    setNacionalidadFilter('');
    setSegundaNacionalidadFilter('');
    setOrigenEtnicoFilter([]);

    // Filtres situation
    setSeleccionNacionalFilter('');
    setEquipoActualFilter([]);
    setCompeticionFilter([]);

    // Filtres expérience
    setAñoDraftFilter('');
    setPosicionDraftFilter([]);
    setEquipoPreDraftFilter('');
    setPitFilter('');
    setEurocampFilter('');
    setSlvegasFilter('');
    setNbaExperienceFilter([]);
    setExperienciaAcbFilter([]);
    setExperiencaEuropaFilter([]);
    setExperienca_fueraFilter([]);
    setExperienca_ncaaFilter([]);

    // Filtres contrat
    setRangoPreciosFilter([]);
    setContratoFilter([]);
    setBuyoutFilter([]);

    // Filtres mesures
    setAlturaFilter('');
    setAlturaZapatosFilter('');
    setEnvergaduraFilter('');
    setAlcanceFilter('');
    setSaltoVerticalMaxFilter('');
    setSaltoVerticalPieFilter('');
    setMedidasPesoFilter('');
    setGrasaCorporalFilter('');
    setLongitudManoFilter('');
    setAnchoManoFilter('');
    setPressBancaFilter('');
    setAgilidadFilter('');
    setSprintCuartoFilter('');

    // Filtres estadísticas
    setGamesFilter('');
    setMinutesFilter('');
    setPointsFilter('');
    setTwoFGPercentFilter('');
    setThreeFGPercentFilter('');
    setFreeThrowPercentFilter('');
    setRebOffFilter('');
    setRebDefFilter('');
    setRebTotFilter('');
    setAssistsFilter('');
    setFoulsFilter('');
    setBlocksFilter('');
    setStealsFilter('');
    setTurnoversFilter('');
    setRankingFilter('');

    // Effacer aussi du localStorage
    localStorage.removeItem('playerListFilters');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress size={80} />
      </Box>
    );
  }

  return (
    <PlayerComparisonProvider filteredPlayers={filteredPlayers}>
      <Box sx={{ mt: 0, pb: isMobile ? 7 : 0 }}>
        {userRole === 'super admin' && selectedPlayerIDs.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Button variant="contained" color="error" onClick={handleOpenMultiDeleteDialog}>
            {t('playerList.deleteSelected')} ({selectedPlayerIDs.length})
          </Button>
        </Box>
      )}

      {/* NOUVELLE SECTION : TABS POUR LES FILTRES (desktop uniquement) */}
      {!isMobile && (
        <Paper elevation={0} sx={{ ...cardStatic, mb: 3, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tabs
              value={filterTab}
              onChange={handleFilterTabChange}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                mb: 2,
                '& .MuiTabs-indicator': { backgroundColor: primaryColor },
                '& .MuiTab-root': {
                  minHeight: 48,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  '&.Mui-selected': { color: primaryColor },
                }
              }}
            >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterList sx={{ color: PALETTE.amber }} />
                  <span>{t('playerList.filterTabs.basic')}</span>
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SportsBasketball sx={{ color: PALETTE.emerald }} />
                  <span>{t('playerList.filterTabs.characteristics')}</span>
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Straighten sx={{ color: PALETTE.sky }} />
                  <span>{t('playerList.filterTabs.measurements')}</span>
                </Box>
              }
            />
            {(userRole === 'super admin' || userRole === 'admin pro') && (
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InsertChart sx={{ color: PALETTE.indigo }} />
                    <span>{t('playerList.filterTabs.statistics')}</span>
                  </Box>
                }
              />
            )}
            </Tabs>
            <Button
              variant="outlined"
              color="error"
              startIcon={<RestartAlt />}
              onClick={handleResetAllFilters}
              sx={{
                ml: 2,
                textTransform: 'none',
                borderColor: 'error.main',
                color: 'error.main',
                '&:hover': {
                  borderColor: 'error.dark',
                  backgroundColor: 'error.main',
                  color: 'white'
                }
              }}
            >
              {t('playerList.resetFilters')}
            </Button>
          </Box>

          {/* Contenu des tabs de filtres */}
          <Box sx={{ mt: 2 }}>
            {filterTab === 0 && (
              <PlayerFilters
                isMobile={isMobile}
                openFilterDrawer={openFilterDrawer}
                setOpenFilterDrawer={setOpenFilterDrawer}
                nameFilter={nameFilter}
                setNameFilter={setNameFilter}
                yearFilter={yearFilter}
                setYearFilter={setYearFilter}
                positionFilter={positionFilter}
                setPositionFilter={setPositionFilter}
                groupFilter={groupFilter}
                setGroupFilter={setGroupFilter}
                heightFilter={heightFilter}
                setHeightFilter={setHeightFilter}
                ageFilter={ageFilter}
                setAgeFilter={setAgeFilter}
                playerTypeFilter={playerTypeFilter}
                setPlayerTypeFilter={setPlayerTypeFilter}
                informeFilter={informeFilter}
                setInformeFilter={setInformeFilter}
                scoutFilter={scoutFilter}
                setScoutFilter={setScoutFilter}
                groups={groups}
                groupColors={groupColors}
                verdictFilter={verdictFilter}
                setVerdictFilter={setVerdictFilter}
                competitionFilter={competitionDetailFilter} // AJOUTER
                setCompetitionFilter={setCompetitionDetailFilter}
                forceExpanded={true}
                userRole={userRole}
              />
            )}

            {filterTab === 1 && (
              <PlayerFiltersCaracterísticas
                isMobile={isMobile}
                rolOfensivoFilter={rolOfensivoFilter}
                setRolOfensivoFilter={setRolOfensivoFilter}
                rolOfensivoCounts={rolOfensivoCounts}
                manoDominanteFilter={manoDominanteFilter}
                setManoDominanteFilter={setManoDominanteFilter}
                manoDominanteCounts={manoDominanteCounts}
                categoriaFilter={categoriaFilter}
                setCategoriaFilter={setCategoriaFilter}
                categoriaCounts={categoriaCounts}
                shootingRangeFilter={shootingRangeFilter}
                setShootingRangeFilter={setShootingRangeFilter}
                shootingRangeCounts={shootingRangeCounts}
                rolDefensivoFilter={rolDefensivoFilter}
                setRolDefensivoFilter={setRolDefensivoFilter}
                rolDefensivoCounts={rolDefensivoCounts}
                jeJiTypeFilter={jeJiTypeFilter}
                setJeJiTypeFilter={setJeJiTypeFilter}
                jeJiAttributesFilter={jeJiAttributesFilter}
                setJeJiAttributesFilter={setJeJiAttributesFilter}
                currentVBCRatingFilter={currentVBCRatingFilter}
                setCurrentVBCRatingFilter={setCurrentVBCRatingFilter}
                currentVBCRatingCounts={currentVBCRatingCounts}
                proyeccionVBCRatingFilter={proyeccionVBCRatingFilter}
                setProyeccionVBCRatingFilter={setProyeccionVBCRatingFilter}
                proyeccionVBCRatingCounts={proyeccionVBCRatingCounts}
                flagBehaviourFilter={flagBehaviourFilter}
                setFlagBehaviourFilter={setFlagBehaviourFilter}
                flagBehaviourCounts={flagBehaviourCounts}
                injuryProneFilter={injuryProneFilter}
                setInjuryProneFilter={setInjuryProneFilter}
                injuryProneCounts={injuryProneCounts}
                digitalBehaviourFilter={digitalBehaviourFilter}
                setDigitalBehaviourFilter={setDigitalBehaviourFilter}
                digitalBehaviourCounts={digitalBehaviourCounts}
                comparacionProFilter={comparacionProFilter}
                setComparacionProFilter={setComparacionProFilter}
                nacionalidadFilter={nacionalidadFilter}
                setNacionalidadFilter={setNacionalidadFilter}
                segundaNacionalidadFilter={segundaNacionalidadFilter}
                setSegundaNacionalidadFilter={setSegundaNacionalidadFilter}
                origenEtnicoFilter={origenEtnicoFilter}
                setOrigenEtnicoFilter={setOrigenEtnicoFilter}
                origenEtnicoCounts={origenEtnicoCounts}
                seleccionNacionalFilter={seleccionNacionalFilter}
                setSeleccionNacionalFilter={setSeleccionNacionalFilter}
                equipoActualFilter={equipoActualFilter}
                setEquipoActualFilter={setEquipoActualFilter}
                competicionFilter={competicionFilter}
                setCompeticionFilter={setCompeticionFilter}
                competicionCounts={competicionCounts}
                añoDraftFilter={añoDraftFilter}
                setAñoDraftFilter={setAñoDraftFilter}
                posicionDraftFilter={posicionDraftFilter}
                setPosicionDraftFilter={setPosicionDraftFilter}
                posicionDraftCounts={posicionDraftCounts}
                equipoPreDraftFilter={equipoPreDraftFilter}
                setEquipoPreDraftFilter={setEquipoPreDraftFilter}
                pitFilter={pitFilter}
                setPitFilter={setPitFilter}
                eurocampFilter={eurocampFilter}
                setEurocampFilter={setEurocampFilter}
                slvegasFilter={slvegasFilter}
                setSlvegasFilter={setSlvegasFilter}
                nbaExperienceFilter={nbaExperienceFilter}
                setNbaExperienceFilter={setNbaExperienceFilter}
                nbaExperienceCounts={nbaExperienceCounts}
                experienciaAcbFilter={experienciaAcbFilter}
                setExperienciaAcbFilter={setExperienciaAcbFilter}
                experienciaAcbCounts={experienciaAcbCounts}
                experiencaEuropaFilter={experiencaEuropaFilter}
                setExperiencaEuropaFilter={setExperiencaEuropaFilter}
                experienciaEuropaCounts={experienciaEuropaCounts}
                experienca_fueraFilter={experienca_fueraFilter}
                setExperienca_fueraFilter={setExperienca_fueraFilter}
                experienciaFueraCounts={experienciaFueraCounts}
                experienca_ncaaFilter={experienca_ncaaFilter}
                setExperienca_ncaaFilter={setExperienca_ncaaFilter}
                experienciaNcaaCounts={experienciaNcaaCounts}
                agenciaFilter={agenciaFilter}
                setAgenciaFilter={setAgenciaFilter}
                rangoPreciosFilter={rangoPreciosFilter}
                setRangoPreciosFilter={setRangoPreciosFilter}
                rangoPreciosCounts={rangoPreciosCounts}
                contratoFilter={contratoFilter}
                setContratoFilter={setContratoFilter}
                contratoCounts={contratoCounts}
                buyoutFilter={buyoutFilter}
                setBuyoutFilter={setBuyoutFilter}
                buyoutCounts={buyoutCounts}
                leagueFilter={leagueFilter}
                setLeagueFilter={setLeagueFilter}
                leagueCounts={leagueCounts}
                competitionFilter={competitionFilter}
                setCompetitionFilter={setCompetitionFilter}
                competitionCounts={competitionCounts}
                licenciaFilter={licenciaFilter}
                setLicenciaFilter={setLicenciaFilter}
                licenciaCounts={licenciaCounts}
                uniqueLeagues={uniqueLeagues}
                agencies={agencies}
                agenciaCounts={agenciaCounts}
                jeAttributeCounts={jeAttributeCounts}
                jiAttributeCounts={jiAttributeCounts}
                forceExpanded={true}
              />
            )}

            {filterTab === 2 && (
              <PlayerFiltersMedidas
                isMobile={isMobile}
                alturaFilter={alturaFilter}
                setAlturaFilter={setAlturaFilter}
                alturaZapatosFilter={alturaZapatosFilter}
                setAlturaZapatosFilter={setAlturaZapatosFilter}
                envergaduraFilter={envergaduraFilter}
                setEnvergaduraFilter={setEnvergaduraFilter}
                alcanceFilter={alcanceFilter}
                setAlcanceFilter={setAlcanceFilter}
                saltoVerticalMaxFilter={saltoVerticalMaxFilter}
                setSaltoVerticalMaxFilter={setSaltoVerticalMaxFilter}
                saltoVerticalPieFilter={saltoVerticalPieFilter}
                setSaltoVerticalPieFilter={setSaltoVerticalPieFilter}
                medidasPesoFilter={medidasPesoFilter}
                setMedidasPesoFilter={setMedidasPesoFilter}
                grasaCorporalFilter={grasaCorporalFilter}
                setGrasaCorporalFilter={setGrasaCorporalFilter}
                longitudManoFilter={longitudManoFilter}
                setLongitudManoFilter={setLongitudManoFilter}
                anchoManoFilter={anchoManoFilter}
                setAnchoManoFilter={setAnchoManoFilter}
                pressBancaFilter={pressBancaFilter}
                setPressBancaFilter={setPressBancaFilter}
                agilidadFilter={agilidadFilter}
                setAgilidadFilter={setAgilidadFilter}
                sprintCuartoFilter={sprintCuartoFilter}
                setSprintCuartoFilter={setSprintCuartoFilter}
                forceExpanded={true}
              />
            )}

            {filterTab === 3 && (userRole === 'super admin' || userRole === 'admin pro') && (
              <PlayerFiltersEstadisticas
                isMobile={isMobile}
                gamesFilter={gamesFilter}
                setGamesFilter={setGamesFilter}
                minutesFilter={minutesFilter}
                setMinutesFilter={setMinutesFilter}
                pointsFilter={pointsFilter}
                setPointsFilter={setPointsFilter}
                twoFGPercentFilter={twoFGPercentFilter}
                setTwoFGPercentFilter={setTwoFGPercentFilter}
                threeFGPercentFilter={threeFGPercentFilter}
                setThreeFGPercentFilter={setThreeFGPercentFilter}
                freeThrowPercentFilter={freeThrowPercentFilter}
                setFreeThrowPercentFilter={setFreeThrowPercentFilter}
                rebOffFilter={rebOffFilter}
                setRebOffFilter={setRebOffFilter}
                rebDefFilter={rebDefFilter}
                setRebDefFilter={setRebDefFilter}
                rebTotFilter={rebTotFilter}
                setRebTotFilter={setRebTotFilter}
                assistsFilter={assistsFilter}
                setAssistsFilter={setAssistsFilter}
                foulsFilter={foulsFilter}
                setFoulsFilter={setFoulsFilter}
                blocksFilter={blocksFilter}
                setBlocksFilter={setBlocksFilter}
                stealsFilter={stealsFilter}
                setStealsFilter={setStealsFilter}
                turnoversFilter={turnoversFilter}
                setTurnoversFilter={setTurnoversFilter}
                rankingFilter={rankingFilter}
                setRankingFilter={setRankingFilter}
                forceExpanded={true}
              />
            )}
          </Box>
        </Paper>
      )}

      {/* MOBILE : on garde l'ancienne structure */}
      {isMobile && (
        <>
          <PlayerFilters
            isMobile={isMobile}
            openFilterDrawer={openFilterDrawer}
            setOpenFilterDrawer={setOpenFilterDrawer}
            nameFilter={nameFilter}
            setNameFilter={setNameFilter}
            yearFilter={yearFilter}
            setYearFilter={setYearFilter}
            positionFilter={positionFilter}
            setPositionFilter={setPositionFilter}
            groupFilter={groupFilter}
            setGroupFilter={setGroupFilter}
            heightFilter={heightFilter}
            setHeightFilter={setHeightFilter}
            ageFilter={ageFilter}
            setAgeFilter={setAgeFilter}
            playerTypeFilter={playerTypeFilter}
            setPlayerTypeFilter={setPlayerTypeFilter}
            informeFilter={informeFilter}
            setInformeFilter={setInformeFilter}
            scoutFilter={scoutFilter}
            setScoutFilter={setScoutFilter}
            groups={groups}
            groupColors={groupColors}
            verdictFilter={verdictFilter}
            setVerdictFilter={setVerdictFilter}
            competitionFilter={competitionDetailFilter} // AJOUTER
            setCompetitionFilter={setCompetitionDetailFilter}
            handleResetAllFilters={handleResetAllFilters}
            userRole={userRole}
          />
        </>
      )}

      {/* Onglets : "Lista" / "Estadisticas" / "Medidas" / "Calendario" */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTabs-indicator': { backgroundColor: primaryColor },
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              '&.Mui-selected': { color: primaryColor },
            }
          }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormatListBulleted sx={{ color: PALETTE.indigo, fontSize: 20 }} />
                <span>{t('playerList.resultTabs.list')}</span>
              </Box>
            }
          />
          {(userRole === 'super admin' || userRole === 'admin pro') && (
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BarChart sx={{ color: PALETTE.violet, fontSize: 20 }} />
                  <span>{t('playerList.resultTabs.statistics')}</span>
                </Box>
              }
            />
          )}
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Straighten sx={{ color: PALETTE.sky, fontSize: 20 }} />
                <span>{t('playerList.resultTabs.measurements')}</span>
              </Box>
            }
          />
          {(userRole === 'super admin' || userRole === 'admin pro') && (
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarMonth sx={{ color: PALETTE.emerald, fontSize: 20 }} />
                  <span>{t('playerList.resultTabs.calendar')}</span>
                </Box>
              }
            />
          )}
        </Tabs>
      </Box>

      {/* Contenu selon l'onglet actif  */}
      {/* 1) Onglet 0 => Liste */}
      {activeTab === 0 && (
        <PlayerResults
          filteredPlayers={filteredPlayers}
          page={page}
          rowsPerPage={rowsPerPage}
          isMobile={isMobile}
          handleViewPlayer={handleViewPlayer}
          handleDeleteClick={handleDeleteClick}
          userRole={userRole}
          selectedPlayerIDs={selectedPlayerIDs}
          handleSelectPlayer={handleSelectPlayer}
          groupColors={groupColors}
          mediaUrl={mediaUrl}
          timestamp={timestamp}
          allChecked={
            filteredPlayers.length > 0 &&
            filteredPlayers.every((p) => selectedPlayerIDs.includes(p.id))
          }
          isIndeterminate={
            selectedPlayerIDs.length > 0 &&
            selectedPlayerIDs.length < filteredPlayers.length
          }
          handleSelectAll={handleSelectAll}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          currentUserId={currentUserId}
        />
      )}

      {/* 2) Onglet 1 => Stats (si role admin pro ou super admin) */}
      {activeTab === 1 && (userRole === 'super admin' || userRole === 'admin pro') && (
        <StatsResults
          filteredPlayers={filteredPlayers}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          mediaUrl={mediaUrl}
          timestamp={timestamp}
          isMobile={isMobile}
        />
      )}

      {/* 3) Onglet Medidas => si on a 4 onglets, c'est l'index 2 ou 1 si pas admin */}
      {((userRole === 'super admin' || userRole === 'admin pro')
        ? activeTab === 2
        : activeTab === 1) && (
        <MedidasResults
          filteredPlayers={filteredPlayers}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          mediaUrl={mediaUrl}
          timestamp={timestamp}
          isMobile={isMobile}
        />
      )}

      {/* 4) Onglet Calendario => index 3 si userRole admin pro / super admin */}
      {((userRole === 'super admin' || userRole === 'admin pro') ? activeTab === 3 : false) && (
        <CalendarioResults
          filteredPlayers={filteredPlayers}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          mediaUrl={mediaUrl}
          timestamp={timestamp}
          isMobile={isMobile}
        />
      )}

      {/* Dialogues de suppression  */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>{t('playerList.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('playerList.deleteDialog.message', { name: playerToDelete?.nom })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>{t('playerList.deleteDialog.cancel')}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {t('playerList.deleteDialog.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={multiDeleteDialogOpen} onClose={handleCloseMultiDeleteDialog}>
        <DialogTitle>{t('playerList.deleteDialog.multipleTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('playerList.deleteDialog.multipleMessage', { count: selectedPlayerIDs.length })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMultiDeleteDialog}>{t('playerList.deleteDialog.cancel')}</Button>
          <Button onClick={handleConfirmMultipleDelete} color="error" variant="contained">
            {t('playerList.deleteDialog.delete')}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </PlayerComparisonProvider>
  );
};

export default PlayerList;